"use client";

/**
 * series key 配列の参照再利用可否を判定する。
 *
 * @remarks
 * legend click と menu toggle が同じ state を共有するため、
 * 並びも含めて変化がないときは React state を維持して再描画を抑える。
 */
export function areStringArraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

/**
 * 初期表示時に有効化する系列キーを解決する。
 *
 * @remarks
 * 明示指定が無効なときだけ全系列へ戻し、ユーザーが後から all-hidden にした状態は
 * 別の reconcile/toggle ロジックで維持する。
 */
export function resolveInitialVisibleSeriesKeys(args: {
  allSeriesKeys: string[];
  defaultVisibleSeriesKeys?: string[] | null;
}): string[] {
  const { allSeriesKeys, defaultVisibleSeriesKeys } = args;
  if (
    Array.isArray(defaultVisibleSeriesKeys) &&
    defaultVisibleSeriesKeys.length > 0
  ) {
    const filtered = defaultVisibleSeriesKeys.filter((key) =>
      allSeriesKeys.includes(key),
    );
    if (filtered.length > 0) return filtered;
  }
  return allSeriesKeys;
}

/**
 * 現在の visible keys から、既に存在しない系列だけを取り除く。
 *
 * @remarks
 * legend click で全系列を非表示にできるようにするため、空配列はそのまま保持する。
 * 新しい系列が増えても、ユーザーが all-hidden を選んでいる間は自動復帰しない。
 */
export function reconcileVisibleSeriesKeys(args: {
  current: string[];
  allSeriesKeys: string[];
}): string[] {
  const { current, allSeriesKeys } = args;
  return current.filter((key) => allSeriesKeys.includes(key));
}

/**
 * 指定系列の visible 状態を切り替える。
 *
 * @remarks
 * AI 可視化では legend click から all-hidden へ到達できる必要があるため、
 * 「最後の 1 系列は消せない」制約は持たせない。
 */
export function toggleVisibleSeriesKeys(args: {
  current: string[];
  allSeriesKeys: string[];
  key: string;
  checked?: boolean;
}): string[] {
  const { current, allSeriesKeys, key, checked } = args;
  const next = new Set(current);
  const shouldShow = checked ?? !next.has(key);

  if (shouldShow) {
    next.add(key);
  } else {
    next.delete(key);
  }

  return allSeriesKeys.filter((seriesKey) => next.has(seriesKey));
}
